// Pure (non-JSX) extraction layer for the DSL reference: turn a
// registered op schema + meta into a flat, render-ready row. Kept
// separate from `components.tsx` so the JSX module exports only
// components (lint: `useComponentExportOnlyModules`) and so the
// extraction is unit-testable without React.

import type { z } from 'zod';

import { type BaseOpMeta, type Effect, type OpExample, opNameOf } from '../op-meta.ts';
import type { EnumDomainResolver } from './types.ts';
import { collectKwargs, describeOf, type KwargDoc, paramsSignature } from './zod-walk.ts';

/**
 * Flat row extracted from an op schema + its registered meta, enriched
 * with the Zod-walked positional/keyword signature. The same shape
 * covers top-level ops, pipeline verbs, agg-context funcs, dashboard
 * tiles/sub-forms — they differ only in which registry holds the meta.
 */
export type OpDocEntry = {
  readonly name: string;
  readonly description: string;
  readonly effect: Effect;
  readonly example: OpExample | undefined;
  readonly typeNote: string | undefined;
  readonly params: string;
  readonly kwargs: readonly KwargDoc[];
};

/** A scope rendered as a section: heading, optional blurb, op list. */
export type OpDocScopeView = {
  readonly name: string;
  readonly description: string | undefined;
  readonly entries: readonly OpDocEntry[];
};

/**
 * Build a doc entry from a schema + its registered meta. Generic over
 * meta type — every scope's meta intersects with `BaseOpMeta`. The
 * optional `resolve` maps enum member sets to a domain name so kwarg
 * types render as `Color` (expanded once in the glossary) rather than
 * the inline value list.
 */
export const entryOf = (
  schema: z.ZodObject,
  meta: BaseOpMeta | undefined,
  resolve?: EnumDomainResolver,
): OpDocEntry => ({
  name: opNameOf(schema),
  // Author-supplied `summary` wins over the Zod `.describe()` text.
  description: meta?.summary ?? describeOf(schema) ?? '',
  effect: meta?.effect ?? 'pure',
  example: meta?.example,
  typeNote: meta?.typeNote,
  params: paramsSignature(schema, resolve),
  kwargs: collectKwargs(schema, resolve),
});

/**
 * The one-line call signature: `(name <pos>… :kw <type> …)`. Optional
 * kwargs are wrapped in `[...]` so required vs optional reads at a
 * glance — this is the single place optionality and type are stated
 * (per-kwarg rows carry only the prose semantics).
 */
export function signatureOf(e: OpDocEntry): string {
  const parts = [
    e.name,
    e.params,
    ...e.kwargs.map((k) => (k.optional ? `[:${k.name} ${k.type}]` : `:${k.name} ${k.type}`)),
  ].filter((p) => p !== '');
  return `(${parts.join(' ')})`;
}
